import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Checkbox, FormControlLabel, Grid, Typography, Chip, Divider } from "@mui/material";
import { FormProvider, RHFRadioGroup, RHFRadioGroupWithErrorText, RHFSelectField, RHFTextField, RHFMultipleSelectField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import MUIModal from "../../components/MuiModal";
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { apiConstants, getGeneric, post } from "../../common/constants";
import { isEscalationAdmin } from "../../components/EscalationManagementV2/emsAccess";
import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import PageTitle from "../../components/PageTitle";
import { emailListProblem, splitEmails } from "../../components/EscalationManagementV2/emailValidation";
import { EmailChips, InfoNote, SettingPanel, configSaveBtnSx } from "../../components/EscalationManagementV2/ConfigUi";
import { MerchantFilterCached, InternalUserPicker } from "../../common/ProshipFormComponents";
import ZoneOverRide from "./ZoneOverride";
import { Box } from "@mui/system";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BlockIcon from '@mui/icons-material/Block';
import ScaleIcon from '@mui/icons-material/Scale';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RuleIcon from '@mui/icons-material/Rule';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';

// OPEN = the ticket was just created (it starts Open); REOPENED = later moved back to Open.
const ESCALATION_STATUS_CATEGORY_OPTIONS = [
    { value: 'OPEN', label: 'Open (ticket created)' },
    { value: 'REOPENED', label: 'Reopened' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'RESOLVED', label: 'Resolved' },
    { value: 'CLOSED', label: 'Closed' },
];


const accordionSx = {
    borderRadius: '12px !important',
    border: '1px solid #e8e8e8',
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 0 12px 0' },
    mb: 1.5,
    overflow: 'hidden',
};

const accordionSummarySx = {
    minHeight: 52,
    '&.Mui-expanded': { minHeight: 52 },
    '& .MuiAccordionSummary-content': { my: 1 },
    '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 },
};


/**
 * Per-merchant routing / pincode / label settings.
 *
 * Reachable two ways:
 *   - embedded (pass `merchantId`)  -> Merchant Setup -> Merchants -> Merchant 360
 *   - standalone (no props)         -> Admin -> Bulk Operations, kept for SA users
 *     who work across merchants and want the picker.
 *
 * When embedded, the picker and page title are suppressed: the merchant is
 * already established by the surrounding page, and asking again was the reason
 * this screen felt like a separate product.
 */
export default function MerchantConfig({ merchantId = null, embedded = false }) {
    const [reload, setReload] = useState(0)
    const [merchantConfig, setMerchantConfig] = useState(null)

    const methods = useForm({
        defaultValues: { merchant: "" }
    });
    const { watch } = methods;
    const picked = watch('merchant');
    const merchant = merchantId || picked;

    useEffect(() => {
        if (merchant) {
            getGeneric(`${apiConstants.MERCHANT_CONFIG}/${merchant}`).then((res) => {
                if (res.status === 200) {
                    setMerchantConfig(res.data)
                }
            })
        }
    }, [merchant, reload]);

    return (
        <>
            {!embedded && <PageTitle>Routing & Pincodes</PageTitle>}

            {/* ── Merchant Selector (standalone only) ── */}
            {!embedded && <Card sx={{ borderRadius: 2.5, border: '1px solid #e8e8e8', boxShadow: 'none', mb: 2.5 }}>
                <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <StorefrontIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
                            Select Merchant
                        </Typography>
                    </Box>
                    <FormProvider methods={methods}>
                        <Box sx={{ width: 300 }}>
                            <MerchantFilterCached />
                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>}

            {/* ── Config Sections ── */}
            {merchant ? (
                <Box>
                    {/* Zone Override */}
                    <Accordion defaultExpanded sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PublicIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Zone Override
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <ZoneOverRide merchant={merchant} isStandalone={false} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Weight Discrepancy */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ScaleIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Weight Discrepancy
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Auto close weight discrepancy cases
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <Discrepancy merchant={merchant} merchantConfig={merchantConfig} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Block Pincode */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BlockIcon sx={{ fontSize: 20, color: '#e65100' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Block Pincode
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Block deliveries to specific areas
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <BlockPincode merchant={merchant} merchantConfig={merchantConfig} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Pickup Location Auto Add */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 20, color: '#2e7d32' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Pickup Location Auto Add
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Auto create pickup locations from shipment data
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <PickupLocationAutoAdd merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Allocation Rules */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RuleIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Allocation Rules
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Cheapest or custom courier allocation
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <AllocationRules merchant={merchant} merchantConfig={merchantConfig} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Shipping Label — Customer Phone */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ContactPhoneOutlinedIcon sx={{ fontSize: 20, color: '#6a1b9a' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Shipping Label — Customer Phone
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Full vs masked customer phone on regenerated labels
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <ShippingLabelPhoneConfig merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Escalation Desk V2 — Auto-Assignment */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonAddAltOutlinedIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Escalation Desk — Auto-Assignment
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — Auto-assign new tickets to an internal user
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <EscalationAutoTagConfig merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} readOnly={!isEscalationAdmin()} />
                        </AccordionDetails>
                    </Accordion>

                    {/* Escalation Desk V2 — Notifications */}
                    <Accordion sx={accordionSx}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NotificationsActiveOutlinedIcon sx={{ fontSize: 20, color: '#1565c0' }} />
                                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#1a1a1a' }}>
                                    Escalation Desk — Notifications
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', ml: 0.5 }}>
                                    — When the requester is emailed (ticket created, reopened, status changes)
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <EscalationNotificationConfig merchant={merchant} merchantConfig={merchantConfig} setReload={setReload} readOnly={!isEscalationAdmin()} />
                        </AccordionDetails>
                    </Accordion>
                </Box>
            ) : (
                <Card sx={{ borderRadius: 2.5, border: '1px solid #e8e8e8', boxShadow: 'none' }}>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <StorefrontIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1.5 }} />
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#616161', mb: 0.5 }}>
                            No Merchant Selected
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e' }}>
                            Please select a merchant above to view and manage configuration options
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </>
    )
}


function AllocationRules({ merchant, merchantConfig }) {
    const [open, setOpen] = useState(false)

    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            rule_type: Yup.string().required("Please select a rule type"),
        })),
        defaultValues: { rule_type: "" }
    });
    const { handleSubmit, setValue, reset } = methods;

    useEffect(() => {
        if (merchantConfig?.allocationRuleType) {
            setValue('rule_type', merchantConfig.allocationRuleType);
        } else {
            reset();
        }
    }, [merchantConfig, setValue, reset]);

    const onSubmit = (data) => {
        if (data?.rule_type === "Cheapest") {
            setOpen(true)
        }
    }

    return (
        <>
            <MUIModal open={open} handleClose={() => setOpen(false)}
                children={<CheapestRules setOpen={setOpen} merchant={merchant} />}
                title={"Select Courier Partner Priority"} />

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{
                    backgroundColor: '#fafbfc',
                    borderRadius: 2,
                    border: '1px solid #f0f0f0',
                    p: 2.5,
                    mb: 2,
                }}>
                    <RHFRadioGroup label="Select Rule Type"
                        name="rule_type"
                        options={[
                            { label: "Cheapest", value: "Cheapest" },
                            { label: "Custom", value: "Custom" },
                        ]}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton
                        variant="contained"
                        size="small"
                        type="submit"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                            backgroundColor: '#1565c0',
                            '&:hover': { backgroundColor: '#0d47a1' },
                        }}
                    >
                        Apply Rules
                    </LoadingButton>
                </Box>
            </FormProvider>
        </>
    )
}


function CheapestRules(props) {
    const { setOpen, merchant } = props;
    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            courier0: Yup.string().required("Please Select Courier"),
        })),
        defaultValues: { courier0: "" }
    });
    const { handleSubmit, reset } = methods;
    const andfilter = [{ "field": "is_deleted", "operator": "eq", "value": '0' }];
    const filter = {
        "andfilter": andfilter,
        "offset": 0,
        "limit": 1000,
        "skipCount": true,
        "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType"
    }
    const { data } = useGetAllCourierPartnersCachedQuery(filter)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/cheapest`, [data?.courier0, data?.courier1, data?.courier2]).then((res) => {
            if (res.status === 200) {
                enqueueSnackbar("Cheapest Rules Updated!", { variant: 'success' });
                setOpen(() => false)
                reset()
            }
        }).catch((err) => console.log(err))
        setIsSubmitting(false)
    }

    const courierOptions = _.orderBy(data, 'name').map((val) => ({
        value: val.prozoId,
        label: val.name
    }));

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>
                    Select up to 3 courier partners in priority order for cheapest rule allocation.
                </Typography>
            </Box>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Chip label="#1" size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22, minWidth: 32, backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                            <Box sx={{ flex: 1 }}>
                                <RHFSelectField width="100%" name="courier0" label="Priority 1 Courier" options={courierOptions} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Chip label="#2" size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22, minWidth: 32, backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                            <Box sx={{ flex: 1 }}>
                                <RHFSelectField width="100%" name="courier1" label="Priority 2 Courier (Optional)" options={courierOptions} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Chip label="#3" size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22, minWidth: 32, backgroundColor: '#f5f5f5', color: '#757575' }} />
                            <Box sx={{ flex: 1 }}>
                                <RHFSelectField width="100%" name="courier2" label="Priority 3 Courier (Optional)" options={courierOptions} />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                            <LoadingButton
                                loading={isSubmitting}
                                variant="contained"
                                size="small"
                                type="submit"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    px: 3,
                                    backgroundColor: '#1565c0',
                                    '&:hover': { backgroundColor: '#0d47a1' },
                                }}
                            >
                                Save Priority Rules
                            </LoadingButton>
                        </Box>
                    </Grid>
                </Grid>
            </FormProvider>
        </Box>
    )
}

function Discrepancy({ merchant, merchantConfig }) {
    const methods = useForm({
        defaultValues: { auto_close: "" }
    });
    const { handleSubmit, reset, setValue } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (merchantConfig?.autoCloseWeightDiscrepancy !== undefined) {
            setValue('auto_close', String(merchantConfig.autoCloseWeightDiscrepancy));
        } else {
            reset();
        }
    }, [merchantConfig, setValue, reset]);

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        const request = {
            field: "autoCloseWeightDiscrepancy",
            value: data?.auto_close
        }
        await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/updatefield`, request).then((res) => {
            if (res.status === 200) {
                enqueueSnackbar("Update Successfully!", { variant: 'success' });
                reset()
            }
        }).catch((err) => console.log(err))
        setIsSubmitting(false)
    }

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{
                backgroundColor: '#fafbfc',
                borderRadius: 2,
                border: '1px solid #f0f0f0',
                p: 2.5,
                mb: 2,
            }}>
                <RHFRadioGroup label="Auto close Weight Discrepancy"
                    name="auto_close"
                    options={[
                        { label: "Enable", value: "true" },
                        { label: "Disable", value: "false" },
                    ]}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                    loading={isSubmitting}
                    variant="contained"
                    size="small"
                    type="submit"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 3,
                        backgroundColor: '#1565c0',
                        '&:hover': { backgroundColor: '#0d47a1' },
                    }}
                >
                    Save Changes
                </LoadingButton>
            </Box>
        </FormProvider>
    )
}

function BlockPincode({ merchant, merchantConfig }) {
    const method = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                blockedPincodes: Yup.mixed()
                    .required("Blocked pincodes are required")
                    .test(
                        'valid-pincodes',
                        'Pincodes must be 6 digits and comma-separated',
                        (value) => {
                            if (!value) return false;
                            const pincodes = Array.isArray(value) ? value : value.split(',').map(pin => pin.trim());
                            return pincodes.every(pin => /^\d{6}$/.test(pin));
                        }
                    ),
            })
        ),
        defaultValues: { blockedPincodes: '' }
    });
    const { handleSubmit, reset, setValue } = method;
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (merchantConfig?.blockedPincodes) {
            const pincodes = Array.isArray(merchantConfig.blockedPincodes)
                ? merchantConfig.blockedPincodes.join(', ')
                : merchantConfig.blockedPincodes;
            setValue('blockedPincodes', pincodes);
        } else {
            reset();
        }
    }, [merchantConfig, setValue, reset]);

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        const request = {
            field: "blockedPincodes",
            value: data?.blockedPincodes
        }
        await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/updatefield`, request).then((res) => {
            if (res.status === 200) {
                enqueueSnackbar("Update Successfully!", { variant: 'success' });
                reset()
            }
        }).catch((err) => console.log(err))
        setIsSubmitting(false)
    }

    return (
        <FormProvider methods={method} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{
                backgroundColor: '#fafbfc',
                borderRadius: 2,
                border: '1px solid #f0f0f0',
                p: 2.5,
                mb: 2,
            }}>
                <RHFTextField
                    width="100%"
                    name="blockedPincodes"
                    label="Blocked Pincodes"
                    placeholder="e.g. 110001, 110002, 400001"
                    multiline
                    minRows={2}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                    loading={isSubmitting}
                    variant="contained"
                    size="small"
                    type="submit"
                    sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 3,
                        backgroundColor: '#e65100',
                        '&:hover': { backgroundColor: '#bf360c' },
                    }}
                >
                    Block Pincodes
                </LoadingButton>
            </Box>
        </FormProvider>
    )
}


export function PickupLocationAutoAdd({ merchant, merchantConfig, setReload }) {
    const validationSchema = Yup.object().shape({
        enabled: Yup.string().required("Please select an option"),
        replaceEnabled: Yup.string().required("Please select an option"),
    });

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { enabled: '', replaceEnabled: '' },
    });

    const { handleSubmit, setValue, reset, watch } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const enabledValue = watch('enabled');
    const isAutoAddEnabled = enabledValue === true || enabledValue === 'true';

    useEffect(() => {
        if (merchantConfig?.pickupDetailsAutoAdd) {
            setValue('enabled', merchantConfig?.pickupDetailsAutoAdd?.enabled);
            setValue('replaceEnabled', merchantConfig?.pickupDetailsAutoAdd?.replaceEnabled);
        } else {
            reset()
        }
    }, [merchantConfig]);

    useEffect(() => {
        if (!isAutoAddEnabled) {
            setValue('replaceEnabled', false);
        }
    }, [isAutoAddEnabled, setValue]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        const request = {
            enabled: data?.enabled === true || data?.enabled === 'true',
            replaceEnabled: data?.replaceEnabled === true || data?.replaceEnabled === 'true',
        };

        await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/pickupDetailsAutoAdd`, request)
            .then((res) => {
                if (res.status === 200) {
                    enqueueSnackbar("Updated Successfully!", { variant: 'success' });
                    setReload((reload) => reload + 1)
                }
            })
            .catch((err) => console.log(err)).finally(() => {
                setIsSubmitting(false)
            })
    };

    return (
        <>
            {/* Info Banner */}
            <Box sx={{
                display: 'flex',
                gap: 1.5,
                backgroundColor: '#e8f5e9',
                borderRadius: 2,
                border: '1px solid #c8e6c9',
                p: 2,
                mb: 2.5,
            }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#2e7d32', mt: 0.2 }} />
                <Box>
                    <Typography sx={{ fontSize: '0.78rem', color: '#1b5e20', lineHeight: 1.6 }}>
                        <strong>Auto Add:</strong> When enabled, a new pickup location is automatically created from the shipment's pickup name.
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#1b5e20', lineHeight: 1.6 }}>
                        <strong>Auto Replace:</strong> When enabled, any existing pickup location will be replaced with the new one (only effective when Auto Add is enabled).
                    </Typography>
                </Box>
            </Box>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            backgroundColor: '#fafbfc',
                            borderRadius: 2,
                            border: '1px solid #f0f0f0',
                            p: 2.5,
                            height: '100%',
                        }}>
                            <RHFRadioGroupWithErrorText
                                label="Auto Add Pickup Location from Pickup Name"
                                name="enabled"
                                options={[
                                    { label: 'Enable', value: true },
                                    { label: 'Disable', value: false },
                                ]}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            backgroundColor: isAutoAddEnabled ? '#fafbfc' : '#f5f5f5',
                            borderRadius: 2,
                            border: `1px solid ${isAutoAddEnabled ? '#f0f0f0' : '#eee'}`,
                            p: 2.5,
                            height: '100%',
                            opacity: isAutoAddEnabled ? 1 : 0.6,
                            transition: 'all 0.2s ease',
                        }}>
                            <RHFRadioGroupWithErrorText
                                label="Auto Replace With Existing Pickup Location"
                                name="replaceEnabled"
                                disabled={!isAutoAddEnabled}
                                options={[
                                    { label: 'Enable', value: true },
                                    { label: 'Disable', value: false },
                                ]}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <LoadingButton
                                loading={isSubmitting}
                                variant="contained"
                                size="small"
                                type="submit"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    px: 3,
                                    backgroundColor: '#2e7d32',
                                    '&:hover': { backgroundColor: '#1b5e20' },
                                }}
                            >
                                Save Settings
                            </LoadingButton>
                        </Box>
                    </Grid>
                </Grid>
            </FormProvider>
        </>
    );
}

function ShippingLabelPhoneConfig({ merchant, merchantConfig, setReload }) {
    const { enqueueSnackbar } = useSnackbar();

    /* ── 1. Merchant toggle: unmask phone on regenerated labels (audited backend API) ── */
    const toggleMethods = useForm({ defaultValues: { unmask: '' } });
    const { handleSubmit: handleToggleSubmit, setValue: setToggleValue, reset: resetToggle } = toggleMethods;
    const [savingToggle, setSavingToggle] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (merchantConfig?.unmaskPhoneOnLabelEnabled !== undefined) {
            setToggleValue('unmask', String(merchantConfig.unmaskPhoneOnLabelEnabled));
        } else {
            resetToggle();
        }
    }, [merchantConfig, setToggleValue, resetToggle]);

    const saveToggle = async (value) => {
        setSavingToggle(true);
        await post(`${apiConstants.MERCHANT_CONFIG}/${merchant}/unmaskPhoneOnLabel?unmaskPhoneOnLabel=${value}`, {})
            .then((res) => {
                if (res.status === 200) {
                    enqueueSnackbar(value === 'true'
                        ? 'Enabled — regenerated labels will print the full customer phone number'
                        : 'Disabled — regenerated labels will print the masked number', { variant: 'success' });
                    setReload((r) => r + 1);
                }
            })
            .catch((err) => enqueueSnackbar(err?.response?.data?.message || 'Update failed', { variant: 'error' }));
        setSavingToggle(false);
        setConfirmOpen(false);
    };

    const onToggleSubmit = (data) => {
        if (!data.unmask) return;
        // Enabling prints customer PII on a physical document — confirm first (the API audits it).
        if (data.unmask === 'true' && merchantConfig?.unmaskPhoneOnLabelEnabled !== true) {
            setConfirmOpen(true);
        } else {
            saveToggle(data.unmask);
        }
    };

    return (
        <>
            {/* PII confirmation before enabling (backend audits old/new value + acting user) */}
            <MUIModal open={confirmOpen} handleClose={() => setConfirmOpen(false)} title='Show full phone on labels?'
                children={
                    <Box sx={{ p: 1 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#424242', mb: 2.5 }}>
                            Labels for this merchant will print the customer's <strong>full phone number</strong>.
                            This action is audited. Continue?
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <LoadingButton size='small' onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>
                                Cancel
                            </LoadingButton>
                            <LoadingButton loading={savingToggle} size='small' variant='contained' color='error'
                                onClick={() => saveToggle('true')} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                Yes, show full number
                            </LoadingButton>
                        </Box>
                    </Box>
                } />

            {/* Info banner — behavior matrix from the backend integration guide */}
            <Box sx={{
                display: 'flex', gap: 1.5, backgroundColor: '#f3e5f5', borderRadius: 2,
                border: '1px solid #e1bee7', p: 2, mb: 2.5,
            }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, color: '#6a1b9a', mt: 0.2 }} />
                <Box>
                    <Typography sx={{ fontSize: '0.78rem', color: '#4a148c', lineHeight: 1.6 }}>
                        <strong>First labels always carry the full number</strong> (rendered before masking runs at order creation).
                        This toggle governs what <strong>regenerated</strong> labels print: enabled = full number, disabled = masked (******1234).
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#4a148c', lineHeight: 1.6 }}>
                        Takes effect immediately for new/regenerated labels; other prod nodes may serve the old setting for up to
                        1 day (config cache). When enabled, the phone line is force-shown on templates that support it.
                    </Typography>
                </Box>
            </Box>

            <FormProvider methods={toggleMethods} onSubmit={handleToggleSubmit(onToggleSubmit)}>
                <Box sx={{
                    backgroundColor: '#fafbfc', borderRadius: 2, border: '1px solid #f0f0f0', p: 2.5, mb: 2,
                }}>
                    <RHFRadioGroup label='Show full customer phone on shipping labels'
                        name='unmask'
                        options={[
                            { label: 'Enable', value: 'true' },
                            { label: 'Disable', value: 'false' },
                        ]}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <LoadingButton loading={savingToggle} variant='contained' size='small' type='submit'
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3,
                            backgroundColor: '#6a1b9a', '&:hover': { backgroundColor: '#4a148c' },
                        }}>
                        Save Phone Setting
                    </LoadingButton>
                </Box>
            </FormProvider>
        </>
    );
}

// The Auto Tagging & Email Config tab can save one setting for many merchants; the server takes
// up to this many per request, so a bigger pick goes in batches.
const BULK_SETTINGS_BATCH = 200;

/** Saves one setting for every merchant in merchantIds. Resolves to { updated, failed: [{merchantId, reason}] };
 *  if a batch fails outright, rejects with the error, its savedBefore = merchants already saved. */
async function saveForMerchants(kind, merchantIds, body) {
    let updated = 0;
    const failed = [];
    for (let i = 0; i < merchantIds.length; i += BULK_SETTINGS_BATCH) {
        try {
            const res = await post(`${apiConstants.ESCALATION_MERCHANT_SETTINGS_V2}/bulk/${kind}`,
                { ...body, merchantIds: merchantIds.slice(i, i + BULK_SETTINGS_BATCH) });
            updated += res?.data?.updated || 0;
            failed.push(...(res?.data?.failed || []));
        } catch (err) {
            err.savedBefore = updated;
            throw err;
        }
    }
    return { updated, failed };
}

const serverMessage = (err) => {
    const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : '');
    return msg ? String(msg).replace(/^(Something went wrong-|Input Data Error:)\s*/, '') : '';
};

/** Snackbars for a several-merchant save. */
const reportBulkSave = (enqueueSnackbar, total, promise, what) => promise
    .then(({ updated, failed }) => {
        enqueueSnackbar(`${what} saved for ${updated} of ${total} merchants`, { variant: failed.length ? 'warning' : 'success' });
        if (failed.length) enqueueSnackbar(`Not saved for ${failed.length}: ${failed.slice(0, 3).map((f) => f.reason).join('; ')}`, { variant: 'error' });
        return true;
    })
    .catch((err) => {
        const msg = serverMessage(err) || `Couldn't save the ${what.toLowerCase()}`;
        enqueueSnackbar(err?.savedBefore ? `Saved for ${err.savedBefore} of ${total} merchants, then: ${msg}` : msg, { variant: 'error' });
        return false;
    });

/** merchantIds (two or more): one setting saved for all of them, starting from a blank form. */
export function EscalationAutoTagConfig({ merchant, merchantConfig, setReload, readOnly = false, stacked = false, merchantIds }) {
    const bulk = Array.isArray(merchantIds) && merchantIds.length > 1;
    const methods = useForm({
        defaultValues: { enabled: '', assignedUserIds: [] },
    });
    const { handleSubmit, setValue, reset, watch } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const enabledValue = watch('enabled');
    const isEnabled = enabledValue === true || enabledValue === 'true';
    const isDisabled = enabledValue === false || enabledValue === 'false';
    const pickedCount = (watch('assignedUserIds') || []).length;

    useEffect(() => {
        if (merchantConfig?.escalationAutoTagConfig) {
            setValue('enabled', merchantConfig.escalationAutoTagConfig.enabled);
            setValue('assignedUserIds', merchantConfig.escalationAutoTagConfig.assignedUserIds || []);
        } else {
            reset();
        }
    }, [merchantConfig, reset, setValue]);

    const onSubmit = async (data) => {
        const enabled = data?.enabled === true || data?.enabled === 'true';
        const assignedUserIds = data?.assignedUserIds || [];
        if (enabled && assignedUserIds.length === 0) {
            enqueueSnackbar("Select at least one user to assign tickets to", { variant: 'error' });
            return;
        }
        setIsSubmitting(true);
        const request = { enabled, assignedUserIds };
        if (bulk) {
            const ok = await reportBulkSave(enqueueSnackbar, merchantIds.length,
                saveForMerchants('auto-tag', merchantIds, { autoTag: request }), 'Auto tagging');
            setIsSubmitting(false);
            if (ok) setReload((reload) => reload + 1);
            return;
        }
        await post(`${apiConstants.ESCALATION_MERCHANT_SETTINGS_V2}/${merchant}/auto-tag`, request)
            .then((res) => {
                if (res.status === 200) {
                    enqueueSnackbar("Updated Successfully!", { variant: 'success' });
                    setReload((reload) => reload + 1);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : '');
                enqueueSnackbar(msg ? String(msg).replace(/^(Something went wrong-|Input Data Error:)\s*/, '') : "Couldn't save the auto-assign settings", { variant: 'error' });
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <InfoNote>
                {readOnly && <b>Only Escalation admins can change these settings. </b>}
                {bulk && <b>Saving applies this to all {merchantIds.length} selected merchants and replaces what each has now. </b>}
                When enabled, every new escalation ticket for this merchant is automatically assigned to
                whichever selected user currently has the fewest active (open/pending) tickets — ties go to
                whoever is listed first. Only users who already have Escalation Desk access are shown below.
            </InfoNote>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={stacked ? 12 : 6}>
                        <SettingPanel tone={isEnabled ? 'on' : isDisabled ? 'off' : 'neutral'}
                            helper={isEnabled ? 'New tickets are assigned automatically.'
                                : isDisabled ? "New tickets aren't auto-assigned." : 'Not set yet. New tickets aren\'t auto-assigned.'}>
                            <RHFRadioGroupWithErrorText
                                label="Auto-Assign New Tickets"
                                name="enabled"
                                disabled={readOnly}
                                options={[
                                    { label: 'Enable', value: true },
                                    { label: 'Disable', value: false },
                                ]}
                            />
                        </SettingPanel>
                    </Grid>
                    <Grid item xs={12} md={stacked ? 12 : 6}>
                        <SettingPanel muted={!isEnabled} tone={isEnabled && pickedCount ? 'on' : 'neutral'}
                            helper={!isEnabled ? 'Enable auto-assign to choose who gets new tickets.'
                                : pickedCount ? `The least busy of these ${pickedCount} ${pickedCount === 1 ? 'person' : 'people'} gets each new ticket.`
                                    : 'Pick at least one person before saving.'}>
                            <InternalUserPicker name="assignedUserIds" label="Assign To (pick one or more)" multiple disabled={!isEnabled || readOnly} />
                        </SettingPanel>
                    </Grid>
                    {!readOnly && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <LoadingButton
                                loading={isSubmitting} variant="contained" size="small" type="submit"
                                sx={configSaveBtnSx}
                            >
                                Save Settings
                            </LoadingButton>
                        </Box>
                    </Grid>
                    )}
                </Grid>
            </FormProvider>
        </>
    );
}

/** merchantIds (two or more): one setting saved for all of them; each keeps its own CC list unless replaced. */
export function EscalationNotificationConfig({ merchant, merchantConfig, setReload, readOnly = false, stacked = false, merchantIds }) {
    const bulk = Array.isArray(merchantIds) && merchantIds.length > 1;
    const [replaceCc, setReplaceCc] = useState(false);
    const methods = useForm({
        resolver: yupResolver(Yup.object().shape({
            additionalEmails: Yup.string().test('emails', (v) => emailListProblem(v?.value) || 'Invalid email',
                (value) => !emailListProblem(value)),
        })),
        defaultValues: { enabled: '', additionalEmails: '', notifyOnStatusCategories: [] },
    });
    const { handleSubmit, setValue, reset, watch } = methods;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const enabledValue = watch('enabled');
    const isEnabled = enabledValue === true || enabledValue === 'true';
    const isDisabled = enabledValue === false || enabledValue === 'false';
    const ccText = watch('additionalEmails');
    const ccEmails = emailListProblem(ccText) ? [] : splitEmails(ccText);
    const statusCount = (watch('notifyOnStatusCategories') || []).length;

    useEffect(() => {
        const config = merchantConfig?.escalationNotificationConfig;
        if (config) {
            setValue('enabled', config.enabled);
            setValue('additionalEmails', (config.additionalEmails || []).join(', '));
            // Unset on the backend means "every category" — default the form to all of them
            // checked so saving without touching this field doesn't silently narrow it.
            setValue('notifyOnStatusCategories', config.notifyOnStatusCategories
                ?? ESCALATION_STATUS_CATEGORY_OPTIONS.map(o => o.value));
        } else {
            reset();
            setValue('notifyOnStatusCategories', ESCALATION_STATUS_CATEGORY_OPTIONS.map(o => o.value));
        }
    }, [merchantConfig, reset, setValue]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        const emails = splitEmails(data?.additionalEmails);
        const request = {
            enabled: data?.enabled === true || data?.enabled === 'true',
            additionalEmails: emails,
            notifyOnStatusCategories: data?.notifyOnStatusCategories || [],
        };
        if (bulk) {
            const ok = await reportBulkSave(enqueueSnackbar, merchantIds.length,
                saveForMerchants('notifications', merchantIds, { notifications: request, keepAdditionalEmails: !replaceCc }), 'Email settings');
            setIsSubmitting(false);
            if (ok) setReload((reload) => reload + 1);
            return;
        }
        await post(`${apiConstants.ESCALATION_MERCHANT_SETTINGS_V2}/${merchant}/notifications`, request)
            .then((res) => {
                if (res.status === 200) {
                    enqueueSnackbar("Updated Successfully!", { variant: 'success' });
                    setReload((reload) => reload + 1);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : '');
                enqueueSnackbar(msg ? String(msg).replace(/^(Something went wrong-|Input Data Error:)\s*/, '') : "Couldn't save the email settings", { variant: 'error' });
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            <InfoNote>
                {readOnly && <b>Only Escalation admins can change these settings. </b>}
                {bulk && <b>Saving applies this to all {merchantIds.length} selected merchants and replaces what each has now (CC emails only if you tick the box below). </b>}
                Controls the emails sent to this merchant's external requesters. Pick when they should be
                emailed: Open = when the ticket is created, Reopened = when it's moved back to Open, and
                Pending / Resolved / Closed = when it moves into that status. Remarks never send an email on
                their own — the latest External remarks are included in these emails. Disable stops every
                email for this merchant.
            </InfoNote>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={stacked ? 12 : 4}>
                        <SettingPanel tone={isEnabled ? 'on' : isDisabled ? 'off' : 'neutral'}
                            helper={isEnabled ? 'Requesters are emailed at the statuses picked under Notify When.'
                                : isDisabled ? 'No ticket emails go out for this merchant.' : 'Not set yet.'}>
                            <RHFRadioGroupWithErrorText
                                label="Ticket Emails"
                                name="enabled"
                                disabled={readOnly}
                                options={[
                                    { label: 'Enable', value: true },
                                    { label: 'Disable', value: false },
                                ]}
                            />
                        </SettingPanel>
                    </Grid>
                    <Grid item xs={12} md={stacked ? 12 : 4}>
                        <SettingPanel muted={!isEnabled} tone={isEnabled && statusCount ? 'on' : 'neutral'}
                            helper={!isEnabled ? 'Applies once ticket emails are enabled.'
                                : statusCount ? `An email goes out when a ticket reaches any of these ${statusCount} statuses.`
                                    : 'No status picked, so no ticket emails go out.'}>
                            <RHFMultipleSelectField
                                name="notifyOnStatusCategories"
                                label="Notify When"
                                width="100%"
                                fullWidth
                                disabled={!isEnabled || readOnly}
                                options={ESCALATION_STATUS_CATEGORY_OPTIONS}
                            />
                        </SettingPanel>
                    </Grid>
                    <Grid item xs={12} md={stacked ? 12 : 4}>
                        <SettingPanel muted={!isEnabled}
                            helper={!isEnabled ? 'Applies once ticket emails are enabled.'
                                : bulk && !replaceCc ? "Each merchant keeps its own CC emails."
                                : ccEmails.length ? `Copied on every ticket email for ${bulk ? 'these merchants' : 'this merchant'}.` : 'Optional. Separate addresses with commas.'}>
                            {bulk && (
                                <FormControlLabel sx={{ mb: 1 }}
                                    control={<Checkbox size="small" checked={replaceCc} disabled={!isEnabled || readOnly} onChange={(e) => setReplaceCc(e.target.checked)} />}
                                    label={<Typography sx={{ fontSize: 13 }}>Replace each merchant&apos;s CC emails with these</Typography>} />
                            )}
                            <RHFTextField
                                width="100%"
                                name="additionalEmails"
                                label="Additional CC Emails"
                                placeholder="e.g. support@merchant.com, ops@merchant.com"
                                multiline
                                minRows={2}
                                disabled={!isEnabled || readOnly || (bulk && !replaceCc)}
                            />
                            {ccEmails.length > 0 && <Box sx={{ mt: 1 }}><EmailChips emails={ccEmails} /></Box>}
                        </SettingPanel>
                    </Grid>
                    {!readOnly && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <LoadingButton
                                loading={isSubmitting} variant="contained" size="small" type="submit"
                                sx={configSaveBtnSx}
                            >
                                Save Settings
                            </LoadingButton>
                        </Box>
                    </Grid>
                    )}
                </Grid>
            </FormProvider>
        </>
    );
}
